import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useState } from 'react';

import { userService } from '../../application/UserService';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseValidateUserNewPasswordToken {
  email: string;
}

type UseResetUserPasswordResponse = ServiceResponse<({ email }: UseValidateUserNewPasswordToken) => void, void>;

// Hooks act as 'Adapter' layer.
export const useResetUserPassword = (): UseResetUserPasswordResponse => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const resetPassword = async ({ email }: UseValidateUserNewPasswordToken) => {
    try {
      setIsLoading(true);
      await userService.fetchResetPassword({ email });
      setIsLoading(false);
      setIsError(false);
      setIsSuccess(true);
      showSnackbarMessage({ type: `success`, messageIntlId: `auth.recovery.request.email_sent` });
    } catch (err: any) {
      setIsLoading(false);
      setIsError(true);
      setIsSuccess(false);

      if (err?.response?.status === 401) {
        sessionExpiredLogout();
        return;
      }

      if (err?.response?.status === 429) {
        showSnackbarMessage({
          type: `warning`,
          autoHideDuration: 6000,
          messageIntlId: `auth.recovery.request.user_new_password_request_already_sent`,
        });
        return;
      }

      showSnackbarMessage({
        messageIntlId: `auth.recovery.request.user_not_found`,
      });
    }
  };

  return {
    success: isSuccess,
    error: isError,
    isLoading,
    run: resetPassword,
  };
};
